import { FormField, FormInput, FormSelect, FormPatternInput as AtpFormPatternInput, twSort } from 'atp-react-ui';
import type { UseFormReturn } from 'react-hook-form';

import { FormPasswordInput, FormPatternInput, FormPatternPasswordInput } from '@/_components';
import { UsaStatesOptions } from '@/_config';

import AllowedCards from './allowed-cards.svg?react';
import type { CheckoutFormSchema } from './forms';

const inputStyle = twSort`rounded-none border-secondary-medium text-lg font-light text-secondary placeholder:text-secondary-medium`;
const inputLabelStyle = twSort`font-light text-secondary-dark`;

export function DetailsForm({ form }: { form: UseFormReturn<CheckoutFormSchema> }) {
  return (
    <section className="flex-1 rounded-3xl rounded-tr-none border border-secondary-medium bg-[#F6F3FF] p-14">
      <h3 className="text-2xl font-semibold text-secondary-dark">Account Details</h3>
      <p className="font-sans font-light text-secondary-dark">
        Enter your details. Already a customer?{' '}
        <a href="/login" className="font-medium text-primary underline">
          Log in
        </a>{' '}
        to proceed.
      </p>

      <section className="mt-6 grid grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="first_name"
          render={({ field }) => (
            <FormInput
              className={inputStyle}
              formItemClassName="col-span-2 md:col-span-1"
              labelClassName={inputLabelStyle}
              label="First Name"
              placeholder="Jane"
              {...field}
            />
          )}
        />
        <FormField
          control={form.control}
          name="last_name"
          render={({ field }) => (
            <FormInput
              formItemClassName="col-span-2 md:col-span-1"
              className={inputStyle}
              labelClassName={inputLabelStyle}
              label="Last Name"
              placeholder="Doe"
              {...field}
            />
          )}
        />
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormInput
              formItemClassName="col-span-2 md:col-span-1"
              className={inputStyle}
              labelClassName={inputLabelStyle}
              label="Email"
              placeholder="youremail@rmail.com"
              {...field}
            />
          )}
        />
        <FormField
          control={form.control}
          name="phone"
          render={({ field: { value, onChange } }) => (
            <FormPatternInput
              className={inputStyle}
              formItemClassName="col-span-2 md:col-span-1"
              labelClassName={inputLabelStyle}
              onValueChange={(e) => {
                onChange(e.value);
              }}
              label="Mobile Phone"
              format="+1 (###) ###-####"
              placeholder="+1 (123) 456-7890"
              value={value}
            />
          )}
        />

        <FormField
          control={form.control}
          name="state"
          render={({ field }) => (
            <FormSelect
              className={inputStyle}
              formItemClassName="col-span-2 md:col-span-1"
              labelClassName={inputLabelStyle}
              options={UsaStatesOptions}
              value={field.value.value}
              onChangeSelection={field.onChange}
              label="State"
            />
          )}
        />

        <FormField
          control={form.control}
          name="zip_code"
          render={({ field }) => (
            <AtpFormPatternInput
              className={inputStyle}
              formItemClassName="col-span-2 md:col-span-1"
              labelClassName={inputLabelStyle}
              label="Zip code"
              pattern="#####"
              placeholder="12345"
              onValueChange={field.onChange}
              {...field}
            />
          )}
        />

        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormPasswordInput
              formItemClassName="col-span-2 md:col-span-1"
              buttonClassName="top-8"
              className={inputStyle}
              labelClassName={inputLabelStyle}
              label="Password"
              {...field}
            />
          )}
        />
        <FormField
          control={form.control}
          name="confirm_password"
          render={({ field }) => (
            <FormPasswordInput
              formItemClassName="col-span-2 md:col-span-1"
              buttonClassName="top-8"
              className={inputStyle}
              labelClassName={inputLabelStyle}
              label="Confirm Password"
              {...field}
            />
          )}
        />
        <div className="col-span-2">
          <p className="font-sans text-xs font-light text-secondary-dark">
            Make sure your password has 8+ characters with letters and numbers!
          </p>
        </div>
        <div className="col-span-2">
          <h3 className="text-2xl font-semibold text-secondary-dark">Payment Information</h3>
        </div>

        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormInput
              className={inputStyle}
              labelClassName={inputLabelStyle}
              formItemClassName="col-span-2"
              label="Name on card"
              placeholder="Jane Doe"
              {...field}
            />
          )}
        />

        <FormField
          control={form.control}
          name="card_number"
          render={({ field }) => (
            <AtpFormPatternInput
              className={inputStyle}
              labelClassName={inputLabelStyle}
              formItemClassName="col-span-2"
              label="Card Number"
              pattern="#### #### #### ####"
              placeholder="1234 5678 5678 1234"
              onValueChange={field.onChange}
              {...field}
            />
          )}
        />

        <FormField
          control={form.control}
          name="expiry"
          render={({ field }) => (
            <AtpFormPatternInput
              className={inputStyle}
              labelClassName={inputLabelStyle}
              label="Expiration Date"
              pattern="##/##"
              placeholder="MM/YY"
              onValueChange={field.onChange}
              {...field}
            />
          )}
        />

        <FormField
          control={form.control}
          name="cvv"
          render={({ field }) => (
            <FormPatternPasswordInput
              className={inputStyle}
              labelClassName={inputLabelStyle}
              buttonClassName="top-8"
              label="Security Code"
              pattern="###"
              type="password"
              placeholder="123"
              onValueChange={field.onChange}
              {...field}
            />
          )}
        />
      </section>

      <div className="mt-6 flex flex-col gap-2">
        <p className="font-sans font-light text-secondary-dark">All transactions are secure and encrypted</p>
        <AllowedCards height={20} className="h-5" />
      </div>
    </section>
  );
}
